import { ChevronLeftIcon } from "@heroicons/react/outline";
import Button from "components/lib/Shared/Button";
import Checkbox from "components/lib/Shared/Checkbox";
import ConfirmDialog from "components/lib/Shared/ConfirmDialog";
import ErrorBanner from "components/lib/Shared/ErrorBanner";
import Tabs from "components/lib/Shared/Tabs";
import { Constants } from "config/constants";
import ManualTransactionEditContext from "context/ManualTransactionEditContext";
import UpdateHistoryContext from "context/UpdateHistoryContext";
import { useToast } from "hooks/useToast";
import { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  deleteInitiatedTransactionChangeAsync,
  getAllInitiateTransactionChangeAsync,
} from "state/slices/initiateTransactionChange";
import { closeModal, openModal } from "state/slices/modals";
import isAnnualReturnsDueAfterCompanyStruckOff from "../../../../../utils/annualDueDateAfterCompanyStruckOff.js";
import { tr } from "date-fns/locale";

const subTabs = Constants.UPDATE_COMPANY_CHANGE_TYPES_SELECTIONS;
const deleteConfirmationLabel =
  "Are you sure you want to unselect this? This action will delete the already initiated transaction.";

const ChangeType = (props) => {
  const modalName = "confirm-delete-manual-transaction-from-main-tabs";

  const { handleGoBack, updateParentTabs, removeParentTab, setTab } = props;
  const dispatch = useDispatch();
  const history = useHistory();
  const { toast } = useToast();
  const {
    checkedTabs,
    setCheckedTabs,
    selectionsMade,
    setSelectionsMade,
    isStrikeOffSelected,
    setIsStrikeOffSelected,
    createdTransactionsIds,
    selectionErrors,
    setSelectionErrors,
    isConversionSelected,
    setIsConversionSelected,
    transactions,
    disableForm,
  } = useContext(UpdateHistoryContext);

  const {
    transactionToEdit,
    setTransactionToEdit,
    isInManualTransactionEditContext,
  } = useContext(ManualTransactionEditContext);

  const { company } = useSelector((state) => state.companyDashboardSlice);
  const { modalRow } = useSelector((state) => state.modalsSlice);

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [selectedTab, setSelectedTab] = useState(null);
  const [selectedSubTab, setSelectedSubTab] = useState({});
  const [showInfo, setShowInfo] = useState(false);
  const [checkedTabName, setCheckedTabName] = useState("");
  const [pendingUnselection, setPendingUnselection] = useState(null);

  const initialTabs = [
    { id: "summary", label: "Summary" },
    { id: "registeredAddress", label: "Registered Address" },
    { id: "shareInformation", label: "Share Information" },
    { id: "stakeholderDetails", label: "Stakeholder Details" },
    { id: "beneficialOwners", label: "Beneficial Owners" },
  ];

  const tabs = useMemo(
    () => initialTabs.filter((tab) => checkedTabs.includes(tab.id)),
    [checkedTabs]
  );

  // Validate if a main tab has selections in any of its sub-tabs
  const hasSelectionsForTab = (tabId) => {
    const subTabIds = subTabs[tabId]?.map((subTab) => subTab.id) || [];
    return subTabIds.some(
      (subTabId) => selectionsMade[tabId]?.[subTabId]?.length > 0
    );
  };

  // Check if all checked tabs have selections
  const allCheckedTabsHaveSelections = () => {
    return (
      checkedTabs.length > 0 &&
      checkedTabs.every((tabId) => hasSelectionsForTab(tabId))
    );
  };

  const handleTabSelectionChange = (e) => {
    const { name, checked } = e.target;

    if (checked) {
      if (isStrikeOffSelected && name !== "summary") {
        toast(
          "error",
          "Cannot select other tabs when Strike Off Application is active."
        );
        return;
      }

      if (isConversionSelected && name !== "summary") {
        toast("error", "Cannot select other tabs when Conversion is active.");
        return;
      }

      const updatedTabs = [...new Set([...checkedTabs, name])]; // Ensure no duplicates

      setSelectedSubTab((prev) => ({
        ...prev,
        [name]: subTabs[name][0]?.id || null,
      }));
      setCheckedTabName(name);
      setCheckedTabs(updatedTabs);
    } else {
      // Show confirmation modal in edit mode
      const res = showDiselectModal({ transactions, nameToUnselect: name });
      if (res) {
        setPendingUnselection({ name, checked });
        dispatch(openModal({ modalName, modalRow: { transactionId: res } }));
      } else {
        const updatedTabs = checkedTabs.filter((tab) => tab !== name);
        removeParentTab(name);
        setCheckedTabName("");
        setSelectionsMade((prev) => {
          const { [name]: _, ...rest } = prev;
          return rest;
        });
        if (name === "shareInformation") {
          setShowInfo(false);
        }
        setCheckedTabs(updatedTabs);
      }
    }
  };

  const handleConfirmUnselection = async () => {
    const success = await deleteTransaction(
      modalRow?.transactionId,
      company?._id,
      toast,
      dispatch
    );
    if (success) {
      setTransactionToEdit("");
      const { name, checked } = pendingUnselection;
      const { companyChanges } = selectionsMade?.summary;

      const updatedTabs = checkedTabs.filter((tab) => tab !== name);
      removeParentTab(name);
      setCheckedTabName("");
      setSelectionsMade((prev) => {
        const { [name]: _, ...rest } = prev;
        return rest;
      });
      if (name === "shareInformation") {
        setShowInfo(false);
      }
      setCheckedTabs(updatedTabs);
      dispatch(closeModal(modalName));
      setPendingUnselection(null);
      // If this was a strikeOffApplication unselection, reset strike off state
      if (companyChanges?.includes("strikeOffApplication") && !checked) {
        setIsStrikeOffSelected(false);
      }

      history.push(
        `/companies/${company._id}/dashboard/manage-your-company?subTab=update-company-details&tab=changeType`
      );
    }
  };

  const handleCancelUnselection = () => {
    dispatch(closeModal(modalName));
    setPendingUnselection(null);
  };

  //Effects
  useEffect(() => {
    if (tabs.length > 0) {
      // If current selectedTabIndex is out of bounds, reset to first tab
      if (selectedTabIndex >= tabs.length) {
        setSelectedTabIndex(0);
        setSelectedTab(tabs[0]);
        setSelectedSubTab((prev) => ({
          ...prev,
          [tabs[0].id]: subTabs[tabs[0].id][0]?.id || null,
        }));
      } else {
        setSelectedTab(tabs[selectedTabIndex]);
        setSelectedSubTab((prev) => ({
          ...prev,
          [tabs[selectedTabIndex].id]:
            prev[tabs[selectedTabIndex].id] ||
            subTabs[tabs[selectedTabIndex].id][0]?.id ||
            null,
        }));
      }
    } else {
      setSelectedTab(null);
      setSelectedTabIndex(0);
      setSelectedSubTab({});
    }
  }, [tabs]);

  useEffect(() => {
    if (checkedTabName) {
      const newTabIndex = tabs.findIndex((tab) => tab.id === checkedTabName);
      if (newTabIndex !== -1) {
        setSelectedTabIndex(newTabIndex);
        setSelectedTab(tabs[newTabIndex]);
      }
    }
  }, [tabs]);

  useEffect(() => {
    if (isInManualTransactionEditContext && selectionsMade) {
      // Find the first main tab that has selections
      const mainTabWithSelections = Object.keys(selectionsMade).find(
        (tabId) =>
          selectionsMade[tabId] &&
          Object.values(selectionsMade[tabId]).some(
            (selections) => selections?.length > 0
          )
      );

      if (
        mainTabWithSelections &&
        checkedTabs.includes(mainTabWithSelections)
      ) {
        // Find the first sub-tab within this main tab that has selections
        const subTabWithSelections = Object.keys(
          selectionsMade[mainTabWithSelections]
        ).find(
          (subTabId) =>
            selectionsMade[mainTabWithSelections][subTabId]?.length > 0
        );

        if (subTabWithSelections) {
          // Set the active main tab
          const mainTabIndex = tabs.findIndex(
            (tab) => tab.id === mainTabWithSelections
          );
          if (mainTabIndex !== -1) {
            setSelectedTabIndex(mainTabIndex);
            setSelectedTab(tabs[mainTabIndex]);
          }

          // Set the active sub-tab
          setSelectedSubTab((prev) => ({
            ...prev,
            [mainTabWithSelections]: subTabWithSelections,
          }));
        }
      }
    }
  }, [isInManualTransactionEditContext, transactionToEdit]);

  useEffect(() => {
    dispatch(
      getAllInitiateTransactionChangeAsync({
        companyId: company?._id,
        params: {
          limit: null,
        },
      })
    );
  }, [createdTransactionsIds]);

  const handleSubTabChange = (mainTabId, subTabId, subTabIndex) => {
    setSelectedSubTab((prev) => ({
      ...prev,
      [mainTabId]: subTabId,
    }));
  };

  // Navigation for main tabs
  const handleMainTabNavigation = (direction) => {
    if (direction === "prev" && selectedTabIndex === 0) {
      handleGoBack(); // Go backcloseModal
      return;
    }
    let newIndex;
    if (direction === "next" && selectedTabIndex < tabs.length - 1) {
      newIndex = selectedTabIndex + 1;
    } else if (direction === "prev" && selectedTabIndex > 0) {
      newIndex = selectedTabIndex - 1;
    } else if (direction === "next" && selectedTabIndex === tabs.length - 1) {
      // On last main tab and last sub-tab, validate and update parent tabs
      if (allCheckedTabsHaveSelections()) {
        updateParentTabs();
        setTab(2);
      } else {
        toast(
          "error",
          "Oops! It looks like some checked tabs are missing selections.Please add at least one selection for each tab to move forward."
        );
      }
      return;
    } else {
      return;
    }
    setSelectedTabIndex(newIndex);
    setSelectedTab(tabs[newIndex]);
    setSelectedSubTab((prev) => ({
      ...prev,
      [tabs[newIndex].id]:
        prev[tabs[newIndex].id] || subTabs[tabs[newIndex].id][0]?.id || null,
    }));
  };

  // Navigation for sub-tabs
  const handleSubTabNavigation = (direction) => {
    if (!selectedTab) return;
    const currentSubTabs = subTabs[selectedTab.id];
    const currentSubTabIndex = currentSubTabs.findIndex(
      (subTab) => subTab.id === selectedSubTab[selectedTab.id]
    );
    let newSubTabIndex;
    if (
      direction === "next" &&
      currentSubTabIndex < currentSubTabs.length - 1
    ) {
      newSubTabIndex = currentSubTabIndex + 1;
    } else if (direction === "prev" && currentSubTabIndex > 0) {
      newSubTabIndex = currentSubTabIndex - 1;
    } else {
      return; // No change if at the boundary
    }
    setSelectedSubTab((prev) => ({
      ...prev,
      [selectedTab.id]: currentSubTabs[newSubTabIndex].id,
    }));
  };

  // Sub-components for each sub-tab
  const renderSubTabContent = (mainTabId, subTabId) => {
    switch (mainTabId) {
      case "summary":
        switch (subTabId) {
          case "companyChanges":
            return (
              <div>
                <CheckBoxSelection mainTabId={mainTabId} subTabId={subTabId} />
              </div>
            );
          case "governanceChanges":
            return (
              <div>
                <CheckBoxSelection mainTabId={mainTabId} subTabId={subTabId} />
              </div>
            );
          case "securities":
            return (
              <div>
                <CheckBoxSelection mainTabId={mainTabId} subTabId={subTabId} />
              </div>
            );
          case "annualCompliance":
            return (
              <div>
                <CheckBoxSelection mainTabId={mainTabId} subTabId={subTabId} />
              </div>
            );
          default:
            return null;
        }
      case "registeredAddress":
        switch (subTabId) {
          case "changeOfAddress":
            return (
              <div>
                <CheckBoxSelection mainTabId={mainTabId} subTabId={subTabId} />
              </div>
            );
          default:
            return null;
        }
      case "shareInformation":
        switch (subTabId) {
          case "capitalIncrease":
            return (
              <div>
                <CheckBoxSelection
                  mainTabId={mainTabId}
                  subTabId={subTabId}
                  setShowInfo={setShowInfo}
                />
              </div>
            );
          case "shareholdingChanges":
            return (
              <div>
                <CheckBoxSelection mainTabId={mainTabId} subTabId={subTabId} />
              </div>
            );
          case "sharesManagement":
            return (
              <div>
                <CheckBoxSelection mainTabId={mainTabId} subTabId={subTabId} />
              </div>
            );
          case "capitalReduction":
            return (
              <div>
                <CheckBoxSelection mainTabId={mainTabId} subTabId={subTabId} />
              </div>
            );
          default:
            return null;
        }
      case "stakeholderDetails":
        switch (subTabId) {
          case "appointment":
            return (
              <div>
                <CheckBoxSelection mainTabId={mainTabId} subTabId={subTabId} />
              </div>
            );
          case "changeOfParticulars":
            return (
              <div>
                <CheckBoxSelection mainTabId={mainTabId} subTabId={subTabId} />
              </div>
            );
          case "cessation":
            return (
              <div>
                <CheckBoxSelection mainTabId={mainTabId} subTabId={subTabId} />
              </div>
            );
          default:
            return null;
        }
      case "beneficialOwners":
        switch (subTabId) {
          case "changesBeneficialOwners":
            return (
              <div>
                <CheckBoxSelection mainTabId={mainTabId} subTabId={subTabId} />
              </div>
            );
          default:
            return null;
        }
      default:
        return null;
    }
  };

  // Determine sub-tab index for navigation button visibility
  const currentSubTabIndex = selectedTab
    ? subTabs[selectedTab.id].findIndex(
        (subTab) => subTab.id === selectedSubTab[selectedTab.id]
      )
    : -1;
  const isFirstSubTab = currentSubTabIndex === 0;
  const isLastSubTab =
    selectedTab && currentSubTabIndex === subTabs[selectedTab.id].length - 1;

  const showPreviousMainTab = isFirstSubTab && selectedTabIndex >= 0;

  return (
    <div className="flex flex-col">
      <p className="my-4">
        Select the tabs from which you would like to alter details to activate
        the fields for editing.
      </p>

      <div className="px-4 py-6 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6">
        <div className="flex gap-8">
          {initialTabs.map((tab) => (
            <div className="flex items-center cursor-pointer" key={tab.id}>
              <Checkbox
                name={tab.id}
                label={tab.label}
                checked={checkedTabs.includes(tab.id)}
                onChange={handleTabSelectionChange}
                disabled={
                  ((isStrikeOffSelected ||
                    isConversionSelected ||
                    company?.status ===
                      Constants.ENTITY_STATUS.STRIKE_OFF_ONGOING ||
                    company?.status === Constants.ENTITY_STATUS.STRUCK_OFF) &&
                    tab.id !== "summary") ||
                  disableForm
                }
              />
            </div>
          ))}
          <ConfirmDialog
            name={modalName}
            onConfirm={handleConfirmUnselection}
            onClose={handleCancelUnselection}
            confirmBtnLabel="Delete"
            title="Unselection Confirmation"
          >
            {deleteConfirmationLabel}
          </ConfirmDialog>
        </div>
      </div>
      <div className="mt-4">
        {tabs.length > 0 && (
          <>
            <Tabs
              tabs={tabs}
              selectedTabIndex={selectedTabIndex}
              setSelectedTabIndex={(index) => {
                setSelectedTabIndex(index);
                setSelectedTab(tabs[index]);
                setSelectedSubTab((prev) => ({
                  ...prev,
                  [tabs[index].id]:
                    prev[tabs[index].id] ||
                    subTabs[tabs[index].id][0]?.id ||
                    null,
                }));
              }}
            />

            {selectedTab && (
              <div className="px-4 py-6 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6">
                {showInfo && selectedTab.id === "shareInformation" && (
                  <div className="text-sm w-[93%] font-bold bg-amber-300 flex items-center py-2 px-3 flex-1 rounded-md">
                    Because you have selected capital increase you cannot make
                    any other selection. Any other selection deselects
                    automatically
                  </div>
                )}
                {isStrikeOffSelected && selectedTab.id === "summary" && (
                  <div className="text-sm w-[93%] font-bold bg-amber-300 flex items-center py-2 px-3 flex-1 rounded-md">
                    Because you have selected Strike Off Application, all other
                    selections are disabled. Any other selection deselects
                    automatically
                  </div>
                )}
                {isConversionSelected && selectedTab.id === "summary" && (
                  <div className="text-sm w-[93%] font-bold bg-amber-300 flex items-center py-2 px-3 flex-1 rounded-md">
                    Because you have selected Conversion Application, all other
                    selections are disabled. Any other selection deselects
                    automatically
                  </div>
                )}
                {
                  <ErrorBanner
                    errors={selectionErrors}
                    title="You cannot proceed with the transaction."
                  />
                }
                <Tabs
                  tabs={subTabs[selectedTab.id]}
                  selectedTabIndex={subTabs[selectedTab.id].findIndex(
                    (subTab) => subTab.id === selectedSubTab[selectedTab.id]
                  )}
                  setSelectedTabIndex={(index) =>
                    handleSubTabChange(
                      selectedTab.id,
                      subTabs[selectedTab.id][index].id,
                      index
                    )
                  }
                />
                <div className="mt-4">
                  {renderSubTabContent(
                    selectedTab.id,
                    selectedSubTab[selectedTab.id]
                  )}
                </div>
                <div
                  className={`flex ${
                    isFirstSubTab ? "justify-end" : "justify-between"
                  } items-center mt-12`}
                >
                  {/* Show Previous Sub-Tab only if not on the first sub-tab */}
                  {!isFirstSubTab && (
                    <button
                      type="button"
                      onClick={() => handleSubTabNavigation("prev")}
                      className="flex  p-2 font-semibold rounded text-primary  hover:bg-gray-700 hover:text-white"
                    >
                      <span className="flex items-center mr-4 h-7">
                        <ChevronLeftIcon
                          className="flex-shrink-0 w-5 h-5"
                          aria-hidden="true"
                        />
                      </span>
                      <p className="font-semibold">Previous Tab</p>
                    </button>
                  )}

                  {/* Show Next Sub-Tab only if not on the last sub-tab */}
                  {!isLastSubTab && (
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        onClick={() => handleSubTabNavigation("next")}
                        className="flex px-2 py-2 text-white button button-hover button-disabled button-outline button-focus bg-sflPrimary"
                      >
                        Next Tab
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div
              className={`flex ${
                isLastSubTab &&
                !(isFirstSubTab && selectedTabIndex > 0) &&
                !showPreviousMainTab
                  ? "justify-end"
                  : "justify-between"
              } items-center  my-2`}
            >
              {/* Show Previous Main Tab only if on the first sub-tab and not on the first main tab */}
              {showPreviousMainTab && (
                <button
                  type="button"
                  onClick={() => handleMainTabNavigation("prev")}
                  className="flex items-start float-right p-2 font-semibold rounded text-primary hover:bg-gray-700 hover:text-white"
                >
                  <span className="flex items-center mr-4 h-7">
                    <ChevronLeftIcon
                      className="flex-shrink-0 w-5 h-5"
                      aria-hidden="true"
                    />
                  </span>
                  <p className="font-semibold">Go back</p>
                </button>
              )}
              {/* Show Next Main Tab only if on the last sub-tab and not on the last main tab */}
              {isLastSubTab && (
                <div className="flex  self-end">
                  <Button
                    type="button"
                    onClick={() => handleMainTabNavigation("next")}
                    className="flex px-2 py-2 text-white button button-hover button-disabled button-outline button-focus bg-sflPrimary"
                  >
                    Next Page
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChangeType;

const CheckBoxSelection = (props) => {
  const modalName = "confirm-delete-manual-transaction-from-subtabs";
  const { company, companyOfficials, dashboardRoles, dashboardPermissions } =
    useSelector((state) => state.companyDashboardSlice);

  const { initiatedTransactions } = useSelector(
    (state) => state.initiatedTransactionsSlice
  );

  const { modalRow } = useSelector((state) => state.modalsSlice);
  const {
    transactionToEdit,
    setTransactionToEdit,
    isInManualTransactionEditContext,
  } = useContext(ManualTransactionEditContext);

  const dispatch = useDispatch();
  const history = useHistory();
  const { toast } = useToast();

  const {
    checkedTabs,
    setCheckedTabs,
    selectionsMade,
    setSelectionsMade,
    isStrikeOffSelected,
    isConversionSelected,
    setIsConversionSelected,
    setIsStrikeOffSelected,
    setSelectionErrors,
    dateFrom,
    dateTo,
    transactions,
    disableForm,
  } = useContext(UpdateHistoryContext);
  const { mainTabId, subTabId, setShowInfo } = props;
  const [checkedSelections, setCheckedSelections] = useState(
    selectionsMade[mainTabId]?.[subTabId] || []
  );

  const [pendingUnselection, setPendingUnselection] = useState(null);

  const { PROCESSED, CANCELLED } = Constants.MAINTAIN_COMPANY_REVIEW_STATUSES;

  const filteredTransactions = transactions?.filter((t) => {
    const { companyChangedFields, status } = t;

    // Early returns for quick exits
    if (!status || status === PROCESSED || status === CANCELLED) {
      return false;
    }

    const dateOfChange =
      companyChangedFields?.dateOfChange ||
      companyChangedFields?.restorationDate ||
      companyChangedFields?.strikeOffWithdrawalDate ||
      companyChangedFields?.cancellationDate ||
      companyChangedFields?.dateOfChangeOfFY;

    if (!dateOfChange) return false;

    const changeDate = new Date(dateOfChange);
    return changeDate >= new Date(dateFrom) && changeDate <= new Date(dateTo);
  });

  // Check if capitalIncrease has a selection
  const hasCapitalIncreaseSelection =
    mainTabId === "shareInformation" &&
    subTabId !== "capitalIncrease" &&
    selectionsMade.shareInformation?.capitalIncrease?.length > 0;

  const hasUnissuedShares = () => {
    if (!company?.nominalShares || company.nominalShares.length === 0)
      return false;
    return company.nominalShares.some(
      (shareClass) => shareClass.unissuedShares > 0
    );
  };

  const handleConfirmUnselection = async () => {
    const success = await deleteTransaction(
      modalRow?.transactionId,
      company._id,
      toast,
      dispatch
    );
    if (success) {
      setTransactionToEdit("");
      const { name, checked } = pendingUnselection;
      let updatedSelections = checkedSelections;

      if (
        mainTabId === "summary" &&
        subTabId === "companyChanges" &&
        name === "conversion"
      ) {
        if (!checked) {
          setSelectionsMade({
            summary: { companyChanges: [] },
          });
          setIsConversionSelected(false);
          updatedSelections = [];
          toast(
            "info",
            "Conversion deselected. Other selections are now enabled."
          );
        }
      } else if (
        mainTabId === "summary" &&
        subTabId === "companyChanges" &&
        name === "strikeOffApplication"
      ) {
        if (!checked) {
          setSelectionsMade({
            summary: { companyChanges: [] },
          });
          setIsStrikeOffSelected(false);
          updatedSelections = [];
          toast(
            "info",
            "Strike Off Application deselected. Other selections are now enabled."
          );
        }
      } else if (
        mainTabId === "shareInformation" &&
        subTabId === "capitalIncrease"
      ) {
        setShowInfo(false);
        if (!checked) {
          updatedSelections = [];
          setSelectionsMade((prev) => ({
            ...prev,
            shareInformation: {
              capitalIncrease: [],
            },
          }));
        }
      } else {
        if (!checked) {
          updatedSelections = updatedSelections.filter((tab) => tab !== name);
        }
      }

      setCheckedSelections(updatedSelections);
      setSelectionsMade((prev) => ({
        ...prev,
        [mainTabId]: {
          ...prev[mainTabId],
          [subTabId]: updatedSelections,
        },
      }));
      setPendingUnselection(null);
      dispatch(closeModal(modalName));

      history.push(
        `/companies/${company._id}/dashboard/manage-your-company?subTab=update-company-details&tab=changeType`
      );
    }
  };

  const handleCancelUnselection = () => {
    dispatch(closeModal(modalName));
    setPendingUnselection(null);
  };

  const ApprovedAuthorizations = useSelector((state) =>
    (state.shareAuthorizationSlice?.shares || []).filter(
      (auth) => auth.isApproved === true
    )
  );

  // Filter checkboxes for capitalIncrease sub-tab
  const getFilteredOptions = () => {
    const allOptions =
      subTabs[mainTabId].find((tab) => tab.id === subTabId)?.options || [];
    if (mainTabId === "shareInformation" && subTabId === "capitalIncrease") {
      const isPVT = company?.type === "PVT";
      const shareClassesCount = company?.nominalShares?.length || 0;

      const hasAuthorization = ApprovedAuthorizations.length > 0;
      const unissuedSharesExist = hasUnissuedShares();

      if (isPVT && shareClassesCount === 1) {
        // PVT with one class of shares
        if (unissuedSharesExist) {
          return [
            {
              id: "unissuedShare/PVTwithOneClass",
              label: "Unissued Share/PVT with One Class",
            },
          ];
        } else {
          return [
            {
              id: "PVTwithOneClass",
              label: "PVT with One Class",
            },
          ];
        }
      } else {
        // PVT with multiple classes or PLC
        const baseOptions = [
          {
            id: "privateWithMoreThanOneClass",
            label: "Authorization and Allotment",
          },
          { id: "authorization", label: "Authorization" },
        ];
        if (unissuedSharesExist && hasAuthorization) {
          return [
            ...baseOptions,
            {
              // id: "unissuedShare/existingAuthorization",
              id: "unallotedShares",
              label: "Unissued Share/Existing Authorization",
            },
          ];
        } else if (unissuedSharesExist) {
          return [
            ...baseOptions,
            {
              // id: "unissuedShare", label: "Unissued Share"
              id: "unallotedShares",
              label: "Unissued Share",
            },
          ];
        } else if (hasAuthorization) {
          return [
            ...baseOptions,
            {
              // id: "existingAuthorization", label: "Existing Authorization"
              id: "existingAuth",
              label: "Existing Authorization",
            },
          ];
        } else {
          return baseOptions; // Only Authorization and Allotment, Authorization
        }
      }
    }

    if (mainTabId === "shareInformation" && subTabId === "capitalReduction") {
      const isPLC = company?.type === "PLC";
      if (isPLC) {
        return [{ id: "courtOrder", label: "Court Order" }];
      }
    }

    if (mainTabId === "summary" && subTabId === "companyChanges") {
      const isPLC = company?.type === "PLC";

      if (isPLC) {
        return subTabs.summary
          .find((tab) => tab.id === subTabId)
          ?.options.filter(
            (option) =>
              option.id !== "conversionPVTtoPLC" &&
              option.id !== "noticeToCancelConversionPVTtoPLC"
          );
      } else {
        return subTabs.summary
          .find((tab) => tab.id === subTabId)
          ?.options.filter(
            (option) =>
              option.id !== "conversionPLCtoPVT" &&
              option.id !== "noticeToCancelConversionPLCtoPVT"
          );
      }
    }

    return allOptions; // Return unfiltered options for other sub-tabs
  };

  //Get filtered transaction for the whole hitsory periods
  const filterFilteredTransactions = initiatedTransactions?.items?.filter(
    (t) =>
      t?.status &&
      t.status === Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DRAFT &&
      t?.isForHistory
  );

  //che if strike off or conversion transaction exists for history changes
  const hasStrikeOffTransaction =
    filterFilteredTransactions?.some(
      (t) => t.changeType === Constants.TRANSACTION_CHANGE_TYPES.STRIKE_OFF
    ) || false;
  const hasConversionTransaction =
    filterFilteredTransactions?.some(
      (t) => t.changeType === Constants.TRANSACTION_CHANGE_TYPES.CONVERSION
    ) || false;

  const hasAdministativeRestorationTransaction =
    filterFilteredTransactions?.some(
      (t) =>
        t.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.ADMINISTRATIVE_RESTORATION
    ) || false;

  const hasWithdrawalOfStrikeOffApplicationTransaction =
    filterFilteredTransactions?.some(
      (t) =>
        t.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.WITHDRAWAL_OF_STRIKE_OFF_APPLICATION
    ) || false;

  const hasNoticeToCancelConversionTransaction =
    filterFilteredTransactions?.some(
      (t) =>
        t.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.NOTICE_TO_CANCEL_CONVERSION
    ) || false;

  //check if 6years have lapsed after the company was struck off
  const isSixYearsLapsed = () => {
    const today = new Date();
    const dateOfStruckOff = new Date(company?.effectiveFrom);
    const diffInTime = today.getTime() - dateOfStruckOff.getTime();
    const diffInDays = Math.ceil(diffInTime / (1000 * 3600 * 24));

    return (
      diffInDays >= 1825 &&
      company?.status === Constants.ENTITY_STATUS.STRUCK_OFF
    );
  };

  const handleSelectionChange = (e) => {
    const { name, checked } = e.target;
    let updatedSelections = checkedSelections;

    if (checked) {
      //check if transaction exists the history transaction tabs either batched or single transaction
      const isTransactionExists = checkIfTransactionExists(transactions, name);

      if (
        (hasStrikeOffTransaction || hasConversionTransaction) &&
        [
          "conversionPVTtoPLC",
          "conversionPLCtoPVT",
          "strikeOffApplication",
        ].includes(name)
      ) {
        toast(
          "error",
          `You can only make one   ${
            hasStrikeOffTransaction
              ? "strike-off application"
              : hasConversionTransaction
              ? "conversion"
              : hasStrikeOffTransaction && hasConversionTransaction
              ? "or conversion "
              : ""
          } transaction.`
        );
        return;
      }

      //Handle strike off validation for history changes
      if (
        !(
          hasAdministativeRestorationTransaction ||
          hasWithdrawalOfStrikeOffApplicationTransaction
        ) &&
        name === "strikeOffApplication"
      ) {
        toast(
          "error",
          `You can only make one strike-off application if you have administrative restoration or withdrawal of strike-off application transaction.`
        );
        return;
      }

      //*Handle validation for:
      //administrative restoration
      //notice to cancel conversion
      //withdrawal of strike-off application
      if (hasNoticeToCancelConversionTransaction) {
        if (
          name === "noticeToCancelConversionPVTtoPLC" ||
          name === "noticeToCancelConversionPLCtoPVT"
        ) {
          toast(
            "error",
            `You can only make one notice to cancel conversion transaction.`
          );
          return;
        }
      }

      if (
        hasAdministativeRestorationTransaction &&
        name === "applicationForAdministrativeRestoration"
      ) {
        toast(
          "error",
          `You can only make one notice of withdrawal of strike-off application transaction.`
        );
        return;
      }

      if (
        hasWithdrawalOfStrikeOffApplicationTransaction &&
        name === "noticeOfWithdrawalOfStrikeOffApplication"
      ) {
        toast(
          "error",
          `You can only make one notice of administrative restoration transaction.`
        );
        return;
      }

      if (isTransactionExists) {
        toast("error", "Transaction already exists for this change type.");
        return;
      }

      // if (
      //   name === "annualReturns" &&
      //   isAnnualReturnsDueAfterCompanyStruckOff(company)
      // ) {
      //   // The transaction exists but we're allowing annual returns due after struck off
      //   //return true;
      // } else if (
      //   name === "annualReturns" &&
      //   !isAnnualReturnsDueAfterCompanyStruckOff(company)
      // ) {
      //   toast("error", "The annual returns are not due yet.");
      //   return;
      // }

      if (
        mainTabId === "summary" &&
        subTabId === "companyChanges" &&
        (name === "conversionPVTtoPLC" || name === "conversionPLCtoPVT")
      ) {
        setSelectionsMade({
          summary: { companyChanges: [name] },
        });
        setIsConversionSelected(true);
        updatedSelections = [name];
        toast(
          "info",
          "Conversion selected. All other selections have been cleared."
        );
      } else if (
        mainTabId === "summary" &&
        subTabId === "companyChanges" &&
        name === "strikeOffApplication"
      ) {
        setSelectionsMade({
          summary: { companyChanges: ["strikeOffApplication"] },
        });
        setIsStrikeOffSelected(true);
        updatedSelections = ["strikeOffApplication"];
        toast(
          "info",
          "Strike Off Application selected. All other selections have been cleared."
        );
      } else if (
        mainTabId === "shareInformation" &&
        subTabId === "capitalIncrease"
      ) {
        setShowInfo(checked);
        updatedSelections = [name];
        setSelectionsMade((prev) => ({
          ...prev,
          shareInformation: {
            capitalIncrease: [name],
          },
        }));
      } else {
        updatedSelections = [...checkedSelections, name];
      }

      setCheckedSelections(updatedSelections);
      setSelectionsMade((prev) => ({
        ...prev,
        [mainTabId]: {
          ...prev[mainTabId],
          [subTabId]: updatedSelections,
        },
      }));
    } else {
      // Handle unselection with confirmation modal
      const res = showDiselectModal({ transactions, nameToUnselect: name });

      if (res) {
        setPendingUnselection({
          name,
          checked,
          currentSelections: checkedSelections,
          mainTabId,
          subTabId,
        });
        dispatch(openModal({ modalName, modalRow: { transactionId: res } }));
      } else {
        // Direct unselection if not in edit context
        if (
          mainTabId === "summary" &&
          subTabId === "companyChanges" &&
          (name === "conversionPVTtoPLC" || name === "conversionPLCtoPVT")
        ) {
          setSelectionsMade({
            summary: { companyChanges: [] },
          });
          setIsConversionSelected(false);
          updatedSelections = [];
          toast(
            "info",
            "Conversion deselected. Other selections are now enabled."
          );
        } else if (
          mainTabId === "summary" &&
          subTabId === "companyChanges" &&
          name === "strikeOffApplication"
        ) {
          setSelectionsMade({
            summary: { companyChanges: [] },
          });
          setIsStrikeOffSelected(false);
          updatedSelections = [];
          toast(
            "info",
            "Strike Off Application deselected. Other selections are now enabled."
          );
        } else if (
          mainTabId === "shareInformation" &&
          subTabId === "capitalIncrease"
        ) {
          setShowInfo(false);
          updatedSelections = [];
          setSelectionsMade((prev) => ({
            ...prev,
            shareInformation: {
              capitalIncrease: [],
            },
          }));
        } else {
          updatedSelections = checkedSelections.filter((tab) => tab !== name);
        }

        setCheckedSelections(updatedSelections);
        setSelectionsMade((prev) => ({
          ...prev,
          [mainTabId]: {
            ...prev[mainTabId],
            [subTabId]: updatedSelections,
          },
        }));
      }
    }
  };

  // Sync local state with context if it changes externally
  useEffect(() => {
    setCheckedSelections(selectionsMade[mainTabId]?.[subTabId] || []);
  }, [selectionsMade, mainTabId, subTabId]);

  const isCheckboxDisabled = (name) => {
    let isDisabled = false;

    if (isStrikeOffSelected) {
      if (name === "strikeOffApplication") {
        isDisabled = false;
      } else if (name === "noticeOfWithdrawalOfStrikeOffApplication") {
        isDisabled = false;
      } else if (name === "applicationForAdministrativeRestoration") {
        isDisabled = false;
      } else {
        isDisabled = true;
      }
    } else if (isConversionSelected) {
      if (name === "conversionPVTtoPLC" || name === "conversionPLCtoPVT") {
        isDisabled = false;
      } else if (name === "noticeToCancelConversionPVTtoPLC") {
        isDisabled = false;
      } else {
        isDisabled = true;
      }
    }

    return (
      (hasCapitalIncreaseSelection && mainTabId === "shareInformation") ||
      isDisabled
    );
  };

  return (
    <div className="flex flex-wrap gap-8 ">
      {getFilteredOptions().map((tab) => (
        <div className="flex  items-center cursor-pointer" key={tab.id}>
          <Checkbox
            name={tab.id}
            label={tab.label}
            checked={checkedSelections.includes(tab.id)}
            onChange={handleSelectionChange}
            disabled={disableForm || isCheckboxDisabled(tab.id)}
          />
        </div>
      ))}
      <ConfirmDialog
        name={modalName}
        onConfirm={handleConfirmUnselection}
        onClose={handleCancelUnselection}
        confirmBtnLabel="Delete"
        title="Unselection Confirmation"
      >
        {deleteConfirmationLabel}
      </ConfirmDialog>
    </div>
  );
};

export const deleteTransaction = async (
  transactionId,
  companyId,
  toast,
  dispatch
) => {
  try {
    const result = await dispatch(
      deleteInitiatedTransactionChangeAsync({
        companyId,
        initiateId: transactionId,
      })
    );

    if (result.error) {
      toast("error", "Failed to delete transaction. Please try again.");
      return false;
    } else {
      toast(
        "success",
        result.payload.message || "Transaction deleted successfully."
      );
      return true;
    }
  } catch (error) {
    toast("error", "Failed to delete transaction. Please try again.");
    return false;
  }
};

const checkIfTransactionExists = (transactions, checkedName) => {
  const options = [];

  for (const transaction of transactions) {
    if (
      transaction.changeType ===
      Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_NAME
    ) {
      options.push("changeOfCompanyName");
    } else if (
      transaction.changeType === Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_FY
    ) {
      options.push("accountingReferenceDate");
    } else if (
      transaction.changeType === Constants.TRANSACTION_CHANGE_TYPES.STRIKE_OFF
    ) {
      options.push("strikeOffApplication");
    } else if (
      transaction.changeType === Constants.TRANSACTION_CHANGE_TYPES.CONVERSION
    ) {
      options.push("conversionPVTtoPLC");
      options.push("conversionPLCtoPVT");
    } else if (
      transaction.changeType ===
      Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_ARTICLES
    ) {
      options.push("amendmentOfArticles");
    } else if (
      transaction.changeType ===
      Constants.TRANSACTION_CHANGE_TYPES.YYYY_ANNUAL_RETURNS
    ) {
      options.push("annualReturns");
    } else if (
      transaction.changeType ===
      Constants.TRANSACTION_CHANGE_TYPES.YYYY_FINANCIAL_STATEMENTS
    ) {
      options.push("financialStatements");
    } else if (
      transaction.changeType ===
      Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_REGISTERED_ADDRESS
    ) {
      options.push("changeOfRegisteredAddress");
    } else if (
      transaction.changeType ===
      Constants.TRANSACTION_CHANGE_TYPES.NOTICE_TO_CANCEL_CONVERSION
    ) {
      options.push("noticeToCancelConversionPLCtoPVT");
      options.push("noticeToCancelConversionPVTtoPLC");
    } else if (
      transaction.changeType ===
      Constants.TRANSACTION_CHANGE_TYPES.NOTICE_OF_WITHDRAWAL_OF_STRIKE_OFF
    ) {
      options.push("noticeOfWithdrawalOfStrikeOffApplication");
    } else if (
      transaction.changeType ===
      Constants.TRANSACTION_CHANGE_TYPES.ADMINISTRATIVE_RESTORATION
    ) {
      options.push("applicationForAdministrativeRestoration");
    } else if (
      transaction.changeType ===
      Constants.TRANSACTION_CHANGE_TYPES.DEBENTURE_AND_CHARGES_AMENDMENT
    ) {
      options.push("debentureAndChargesAmendment");
    } else if (
      transaction.changeType ===
      Constants.TRANSACTION_CHANGE_TYPES.DEBENTURE_AND_CHARGES_DISCHARGE
    ) {
      options.push("debentureAndChargesDischarge");
    } else if (
      transaction.changeType ===
      Constants.TRANSACTION_CHANGE_TYPES.DEBENTURE_AND_CHARGES_REGISTRATION
    ) {
      options.push("debentureAndChargesRegistration");
    }
  }

  const foundOption = options.find((option) => option === checkedName);

  return foundOption ? true : false;
};

const showDiselectModal = ({ transactions, nameToUnselect }) => {
  const summary = [
    Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_NAME,
    Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_FY,
    Constants.TRANSACTION_CHANGE_TYPES.STRIKE_OFF,
    Constants.TRANSACTION_CHANGE_TYPES.CONVERSION,
    Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_ARTICLES,
    Constants.TRANSACTION_CHANGE_TYPES.YYYY_ANNUAL_RETURNS,
    Constants.TRANSACTION_CHANGE_TYPES.YYYY_FINANCIAL_STATEMENTS,
  ];
  for (const transaction of transactions) {
    if (
      nameToUnselect === "summary" &&
      summary.includes(transaction.changeType)
    ) {
      return transaction?._id;
    } else if (
      nameToUnselect === "registeredAddress" &&
      transaction.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_REGISTERED_ADDRESS
    ) {
      return transaction?._id;
    } else if (
      nameToUnselect === "changeOfCompanyName" &&
      transaction.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_NAME
    ) {
      return transaction?._id;
    } else if (
      nameToUnselect === "accountingReferenceDate" &&
      transaction.changeType === Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_FY
    ) {
      return transaction?._id;
    } else if (
      nameToUnselect === "strikeOffApplication" &&
      transaction.changeType === Constants.TRANSACTION_CHANGE_TYPES.STRIKE_OFF
    ) {
      return transaction?._id;
    } else if (
      (nameToUnselect === "conversionPVTtoPLC" ||
        nameToUnselect === "conversionPLCtoPVT") &&
      transaction.changeType === Constants.TRANSACTION_CHANGE_TYPES.CONVERSION
    ) {
      return transaction?._id;
    } else if (
      nameToUnselect === "amendmentOfArticles" &&
      transaction.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_ARTICLES
    ) {
      return transaction?._id;
    } else if (
      nameToUnselect === "annualReturns" &&
      transaction.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.YYYY_ANNUAL_RETURNS
    ) {
      return transaction?._id;
    } else if (
      nameToUnselect === "financialStatements" &&
      transaction.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.YYYY_FINANCIAL_STATEMENTS
    ) {
      return transaction?._id;
    } else if (
      nameToUnselect === "changeOfRegisteredAddress" &&
      transaction.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_REGISTERED_ADDRESS
    ) {
      return transaction?._id;
    } else if (
      nameToUnselect === "noticeOfWithdrawalOfStrikeOffApplication" &&
      transaction.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.NOTICE_OF_WITHDRAWAL_OF_STRIKE_OFF
    ) {
      return transaction?._id;
    } else if (
      nameToUnselect === "applicationForAdministrativeRestoration" &&
      transaction.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.ADMINISTRATIVE_RESTORATION
    ) {
      return transaction?._id;
    } else if (
      (nameToUnselect === "noticeToCancelConversionPVTtoPLC" ||
        nameToUnselect === "noticeToCancelConversionPLCtoPVT") &&
      transaction.changeType ===
        Constants.TRANSACTION_CHANGE_TYPES.NOTICE_TO_CANCEL_CONVERSION
    ) {
      return transaction?._id;
    }
  }
  return false;
};
